import React, { useCallback } from "react"
import { ColumnFiltersState } from "@tanstack/react-table"

interface GenericToggleProps {
  columnID: string
  columnFilters: ColumnFiltersState
  setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>
  filterKey: string // e.g. "active" or "deleted"
  filterValue: string // e.g. "1"
  label: string
}

const GenericFilterToggle: React.FC<GenericToggleProps> = ({
  columnID,
  columnFilters,
  setColumnFilters,
  filterKey,
  filterValue,
  label
}) => {
  // Find the existing filter for the columnID column (shared filter)
  const existingFilter = columnFilters.find(filter => filter.id === columnID)

  // Extract the current filter values (if any); assume it is an object of key-value pairs
  const currentValue = React.useMemo(() => {
    return existingFilter && typeof existingFilter.value === "object"
      ? (existingFilter.value as Record<string, string>)
      : {}
  }, [existingFilter])

  // Determine whether the specific key is active
  const isOn = currentValue[filterKey] === filterValue

  const toggleFilter = useCallback(() => {
    setColumnFilters(prev => {
      // Remove any previous filter for columnID
      const others = prev.filter(filter => filter.id !== columnID)
      // Copy the current filter object
      const newValue: Record<string, string> = { ...currentValue }
      if (isOn) {
        // If the filter is on, remove the key
        delete newValue[filterKey]
      } else {
        // Otherwise, add it
        newValue[filterKey] = filterValue
      }
      // If the filter object is non-empty, return it in the new filters array; otherwise, return only the others
      return Object.keys(newValue).length
        ? [...others, { id: columnID, value: newValue }]
        : others
    })
  }, [setColumnFilters, filterKey, filterValue, isOn, currentValue, columnID])

  return (
    <label
      htmlFor={`filter-${columnID}-${filterKey}`}
      className="flex items-center cursor-pointer"
    >
      <input
        id={`filter-${columnID}-${filterKey}`}
        name={`${columnID}-${filterKey}`}
        defaultValue={filterValue}
        checked={isOn}
        onChange={toggleFilter}
        type="checkbox"
        className="h-4 w-4 border-gray-300 rounded text-indigo-500 focus:ring-indigo-400"
      />
      <span className="ml-3 pr-6 text-sm font-medium text-gray-900 whitespace-nowrap">
        {label}
      </span>
    </label>
  )
}

export default GenericFilterToggle
